import React, { useEffect, useState, createRef, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Header } from "../../components/Header";
import CustomButton from "../../components/Button";
import { Smartphone } from "lucide-react";
import {
    handleInputChange,
    handlePaste,
} from "../../../../utils/inputHelpers";
import {
    auth
} from "../../../../api/firebase";
import { getUserFriendlyErrorMessage } from "../../../../utils/errorMessages";
import {
    RecaptchaVerifier,
    PhoneAuthProvider,
    linkWithCredential,
    signOut,
    signInWithCustomToken,
    updatePhoneNumber
} from "firebase/auth";
import authApi from "../../../../api/authApi";
import { consumePostLoginRedirect, persistSessionValue, persistToken } from "../../../../utils/authStorage";

declare global {
    interface Window {
        recaptchaVerifier?: import("firebase/auth").RecaptchaVerifier;
    }
}

const FirebaseMobile = () => {
    const inputRefs = Array(6).fill(null).map(() => createRef<HTMLInputElement>());
    const [inputValuess, setinputValuess] = useState(["", "", "", "", "", ""]);
    const isButtonGray = inputValuess.every((value) => value.length === 1);
    const [resend, setResend] = useState(false);

    const location = useLocation();
    const fromProfile = location.state?.fromProfile || false;
    const new_phone = location.state?.new_phone || '';
    const [phone, setPhone] = useState(() =>
        fromProfile ? new_phone : sessionStorage.getItem("phone") || ""
    );
    const [phoneInput, setPhoneInput] = useState('')

    const navigate = useNavigate();
    const recaptchaDivRef = useRef<HTMLDivElement>(null);
    const verificationIdRef = useRef<string>("");

    useEffect(() => {
        inputRefs[0].current?.focus();

        // Sign in user frontend Firebase SDK
        const token = sessionStorage.getItem("custom_token");
        const init = async () => {
            if (token && !auth.currentUser) {
                await signInWithCustomToken(auth, token)
                    .then(() => console.log("Re-signed in with custom token"))
                    .catch((err) => console.error("Re-authentication failed", err));
            }
            await handleSendMobile();

        };

        init();
    }, []);

    // 
    const handleSendMobile = async () => {
        if (!phone) {
            alert("Missing phone number.");
            return;
        }

        console.log("auth.currentUser", auth.currentUser)

        // ⚠️ Clear any stale verifier
        if (window.recaptchaVerifier) {
            try {
                window.recaptchaVerifier.clear();
            } catch (e) {
                console.warn("Failed to clear old reCAPTCHA:", e);
            }
            delete window.recaptchaVerifier;
        }

        // ✅ Create a fresh one
        if (recaptchaDivRef.current) {
            window.recaptchaVerifier = new RecaptchaVerifier(
                auth,
                recaptchaDivRef.current,
                {
                    size: "invisible",
                    callback: () => console.log("reCAPTCHA verified"),
                }
            );
            await window.recaptchaVerifier.render();
        }

        console.log("phone:", phone);

        try {
            const provider = new PhoneAuthProvider(auth);
            const verificationId = await provider.verifyPhoneNumber(
                phone,
                window.recaptchaVerifier
            );
            verificationIdRef.current = verificationId;
            setResend(true);
            setTimeout(() => setResend(false), 30000);
        } catch (err: any) {
            console.error(err);
            alert(getUserFriendlyErrorMessage(err, "Could not send OTP."));
        }
    };

    const handleVerifyMobile = async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        const otp = inputValuess.join("");

        if (!phone || otp.length !== 6 || !verificationIdRef.current) {
            alert("Missing phone or incomplete OTP.");
            return;
        }

        try {
            const credential = PhoneAuthProvider.credential(
                verificationIdRef.current,
                otp
            );

            const user = auth.currentUser;
            if (!user) throw new Error("User not authenticated");

            let id_token;

            if (fromProfile) {
                await updatePhoneNumber(user, credential); // for profile phone change
                id_token = await user.getIdToken();
            } else {
                const result = await linkWithCredential(user, credential); // for registration
                id_token = await result.user.getIdToken();
            }

            // SET id_token IN SESSION
            persistToken(id_token)

            // Call backend to mark verified
            await authApi.verifyMobileOtp(id_token);

            // Sign out of Firebase phone session (Plan B: Wait to avoid race conditions)
            try {
                await signOut(auth);
            } catch (err) {
                console.warn("Sign out failed, redirecting anyway", err);
            }

            // Redirect after success
            persistSessionValue('just_logged_in', 'true');
            sessionStorage.removeItem('login_cashflow_refresh_started');
            window.location.replace(consumePostLoginRedirect() || "/home");
        } catch (err: any) {
            alert(getUserFriendlyErrorMessage(err, "Mobile verification failed. Please try again."));
        }
    };

    return (
        <div className="flex flex-col min-h-screen bg-gray-50">
            <Header />

            <div ref={recaptchaDivRef} id="recaptcha-container" />

            <div className="flex flex-col items-center justify-center flex-1 px-4 sm:px-0 bg-gray-50">
                <div className="w-full max-w-xl bg-white rounded-lg px-6 sm:px-16 py-10 sm:py-12">
                    <form className="flex flex-col gap-6" autoComplete="off">
                        {/* Icon */}
                        <div className="flex justify-center items-center">
                            <div className="w-14 h-14 rounded-full bg-gray-200 flex items-center justify-center">
                                <Smartphone className="w-6 h-6 text-gray-800" />
                            </div>
                        </div>

                        {/* Headers */}
                        <div className="flex flex-col items-center gap-2 text-center">
                            <h1 className="text-gray-800 text-2xl sm:text-3xl font-bold">
                                Mobile verification
                            </h1>
                            <p className="text-gray-700 font-medium text-sm sm:text-base leading-6">
                                Enter the 6-digit verification code sent to your mobile
                            </p>
                        </div>

                        {/* OTP inputs */}
                        <div className="flex justify-center gap-2 sm:gap-5 text-2xl sm:text-3xl rounded-lg">
                            {inputRefs.map((inputRef, index) => (
                                <input
                                    disabled={!phone}
                                    key={index}
                                    type="text"
                                    ref={inputRef}
                                    className="w-12 h-14 sm:w-16 sm:h-20 text-center text-gray-800 font-medium bg-gray-100 border border-gray-800 rounded-xl focus:border-black focus:outline-black"
                                    maxLength={1}
                                    value={inputValuess[index]}
                                    onChange={(e) =>
                                        handleInputChange(
                                            e,
                                            index,
                                            inputValuess,
                                            setinputValuess,
                                            inputRefs,
                                            inputValuess.length
                                        )
                                    }
                                    onPaste={(e) =>
                                        handlePaste(
                                            e,
                                            inputValuess,
                                            setinputValuess,
                                            inputRefs,
                                            inputValuess.length
                                        )
                                    }
                                />
                            ))}
                        </div>

                        {/* Phone input (fallback) */}
                        {!fromProfile && !phone && (
                            <div className="relative">
                                <input
                                    type="tel"
                                    placeholder="Enter your phone number"
                                    value={phoneInput}
                                    autoComplete="off"
                                    onChange={(e) => setPhoneInput(e.target.value)}
                                    className="border border-gray-400 rounded-md px-4 py-2 text-sm w-full"
                                />
                                <button className="p-1 text-sm border rounded bg-gray-100 absolute right-1 top-1" onClick={() => { setPhone(phoneInput) }}>
                                    Done
                                </button>
                            </div>
                        )}

                        {/* Resend or timer */}
                        {!resend ? (
                            <div className="text-gray-800 text-center text-sm">
                                <span className="underline cursor-pointer" onClick={handleSendMobile}>
                                    {!fromProfile && !phone
                                        ? "Send Code"
                                        : "Resend Code"}
                                </span>
                            </div>
                        ) : (
                            <div className="text-center text-green-700 text-sm">
                                Verification code sent. Resend in 30 seconds
                            </div>
                        )}

                        {/* Submit */}
                        <CustomButton
                            text="Continue"
                            type="submit"
                            color={{
                                buttonColor: isButtonGray ? "bg-gray-800" : "bg-gray-200",
                                textColor: isButtonGray ? "text-white" : "text-gray-800",
                            }}
                            onClick={handleVerifyMobile}
                            disabled={!isButtonGray}
                            className="w-full sm:w-60 h-12 flex items-center justify-center rounded-xl transition duration-200 mx-auto"
                        />
                    </form>
                </div>
            </div>
        </div>
    );
};

export default FirebaseMobile;
